import { formatINR } from "@/utils/formatINR";
import { AlertTriangle } from "lucide-react";
import ScreenshotThumb from "@/components/ScreenshotThumb";
import { computeMachineRequirement } from "@/utils/machineRequirement";

const MACHINE_TYPE_LABELS = { press: "Press", cnc: "CNC", vmc: "VMC", welding: "Welding", other: "Other", cnc_vmc: "CNC/VMC" };

export default function LiveCalcPanel({ calc, threshold = 15, hideMargin = false, targetPrice, rmcReasons = [], screenshotScope = "npd", entityId, screenshotPath, processes = [], monthlyVolume = 0, settledPrice, settledDev }) {
  // Machine requirement — cycle time × monthly volume ÷ capacity (press 260 h/mo, cnc/vmc 520).
  const machineReq = computeMachineRequirement(processes, monthlyVolume);
  const machineBlock = (machineReq.rows.length > 0) ? (
    <div className="mt-4 pt-4 border-t border-slate-200" data-testid="live-calc-machines">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Machine Requirement</div>
      {monthlyVolume > 0 ? (
        <>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[9px] uppercase tracking-wider text-slate-400">
                <th className="text-left font-medium pb-1">Tonnage</th>
                <th className="text-right font-medium pb-1">Hrs/mo</th>
                <th className="text-right font-medium pb-1" title="Fraction of one machine this part uses (shared capacity)">Load</th>
              </tr>
            </thead>
            <tbody>
              {machineReq.rows.map((r) => (
                <tr key={r.machine} className="border-t border-slate-100">
                  <td className="py-1 text-slate-700">{r.machine}
                    <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-slate-100 text-slate-500 uppercase">{MACHINE_TYPE_LABELS[r.type] || r.type}</span>
                  </td>
                  <td className="py-1 text-right font-mono text-slate-600">{r.hours.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                  <td className="py-1 text-right font-mono font-semibold text-indigo-700">{r.machines.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-200">
                <td className="py-1 font-semibold text-slate-800">Total</td>
                <td className="py-1 text-right font-mono text-slate-600">{machineReq.totalHours.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                <td className="py-1 text-right font-mono font-bold text-indigo-700">{machineReq.totalMachines.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
              </tr>
            </tbody>
          </table>
          <div className="text-[10px] text-slate-400 mt-1.5">
            "Load" = fraction of one machine this part uses (shared capacity — not rounded up). At {Number(monthlyVolume).toLocaleString("en-IN")} pcs/mo · Press 260 h/mo · CNC/VMC 520 h/mo.
          </div>
        </>
      ) : (
        <div className="text-[11px] text-slate-400 italic">Add Project Scope volumes and process cycle times to see machines required.</div>
      )}
    </div>
  ) : null;
  // Drawing-screenshot preview — also surfaced here (in addition to the upload
  // section) so it's visible alongside the live cost. Renders nothing until saved.
  const screenshotBlock = (screenshotPath && entityId) ? (
    <div className="mt-4 pt-4 border-t border-slate-200" data-testid="live-calc-screenshot">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Drawing screenshot</div>
      <div className="border border-slate-200 rounded-md bg-slate-50 overflow-hidden h-40 flex items-center justify-center">
        <ScreenshotThumb scope={screenshotScope} entityId={entityId} path={screenshotPath} className="w-full h-full flex items-center justify-center" />
      </div>
    </div>
  ) : null;
  const grand_total = calc?.grand_total || 0;
  // Customer Settled Price: the agreed ₹/pc REPLACES the engine Grand Total; Process Cost flexes
  // so A+B+C+D+E sum to the settled total (RM, scrap & margin stay fixed), exactly like the
  // Customer Settled Price worksheet.
  const hasSettled = settledPrice != null && settledPrice !== "" && Number(settledPrice) > 0;
  const effGrandTotal = hasSettled ? Number(settledPrice) : grand_total;
  // Each cost line's share of the Grand Total (selling price), for the % breakdown.
  const pctOf = (v) => (effGrandTotal > 0 && v != null ? (Number(v) / effGrandTotal) * 100 : null);
  const margin = calc?.margin_amount || 0;
  const marginPct = calc?.margin_pct || 0;
  // Distribute the settled price PROPORTIONALLY across every section (each keeps its engine
  // share), so no single section dominates — the extra is shared, not dumped into Margin.
  const scale = (hasSettled && grand_total > 0) ? (effGrandTotal / grand_total) : 1;
  const sc = (v) => (Number(v) || 0) * scale;        // scale any engine figure to the settled total
  const effOhm = sc(calc?.overheads_and_margin);
  const effMargin = margin * scale;
  const effMarginPct = hasSettled ? marginPct : marginPct;   // proportional → % share unchanged
  const redFlag = effGrandTotal > 0 && effMarginPct < threshold;
  const tool_amortization = calc?.tool_amortization || 0;
  const amortize_tooling = !!calc?.amortize_tooling;
  const tooling_one_time = calc?.tooling_cost_one_time || 0;
  const other_dev_costs = calc?.other_dev_costs || [];
  const other_dev_total = calc?.other_dev_total || 0;
  const total_development_cost = calc?.total_development_cost || 0;
  // Settled dev REPLACES the engine Development/Tooling in-place.
  const hasSettledDev = settledDev != null && settledDev !== "";
  const effDev = hasSettledDev ? Number(settledDev) : total_development_cost;
  // RMC-incomplete: hide totals entirely — a Grand Total without material cost is false.
  if (rmcReasons && rmcReasons.length > 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4 lg:sticky lg:top-4" data-testid="live-calc-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm text-slate-900">Live Calculation</h3>
          <div className="text-[10px] uppercase tracking-wider text-orange-600 bg-orange-50 px-2 py-0.5 rounded">auto</div>
        </div>
        <div className="bg-amber-50 border border-amber-300 rounded-md p-3" data-testid="rmc-incomplete-banner">
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm mb-1">
            <AlertTriangle size={14} /> Costing incomplete — Net RMC cannot be calculated
          </div>
          <ul className="text-xs text-amber-700 list-disc pl-4 space-y-0.5">
            {rmcReasons.map((r) => <li key={r}>{r}</li>)}
          </ul>
          <div className="text-[11px] text-amber-700 mt-2">
            Totals are hidden because they would exclude material cost. Complete Section A to see the price.
          </div>
        </div>
        {screenshotBlock}{machineBlock}
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 lg:sticky lg:top-4" data-testid="live-calc-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm text-slate-900">Live Calculation</h3>
        <div className="text-[10px] uppercase tracking-wider text-orange-600 bg-orange-50 px-2 py-0.5 rounded">auto</div>
      </div>
      <div className="space-y-0.5 text-sm">
        <Row label="Gross RMC" value={sc(calc?.gross_rmc)} indent target="sec-a" />
        <Row label="− Scrap Recovery" value={sc(calc?.scrap_recovery)} indent dim target="sec-a" />
        <Row label="A. Net RMC" value={sc(calc?.net_rmc)} bold section target="sec-a" pct={pctOf(sc(calc?.net_rmc))} />
        <Row label="B. Process Cost" value={sc(calc?.process_cost_total)} bold section target="sec-b" pct={pctOf(sc(calc?.process_cost_total))} />
        <Row label="C. BOP Cost" value={sc(calc?.bop_cost_total)} bold section target="sec-c" pct={pctOf(sc(calc?.bop_cost_total))} />
        <Row label="D. Treatment & Maint" value={sc(calc?.treatment_total)} bold section target="sec-d" pct={pctOf(sc(calc?.treatment_total))} />
        {amortize_tooling && tool_amortization > 0 && (
          <Row label="F. Tool Amortization" value={sc(tool_amortization)} bold section target="sec-d" pct={pctOf(sc(tool_amortization))} />
        )}
        <Row label="Sub-Total" value={sc(calc?.sub_total)} highlight pct={pctOf(sc(calc?.sub_total))} />
        <Row label={hasSettled ? "E. Overheads & Margin (Settled)" : "E. Overheads & Margin"} value={effOhm} bold section target="sec-e" pct={pctOf(effOhm)} />
        {(calc?.custom_adjustments_total || 0) !== 0 && (
          <Row label="F. Custom Adjustments" value={calc?.custom_adjustments_total} bold section target="sec-f" pct={pctOf(calc?.custom_adjustments_total)} />
        )}
        <div className="mt-2 pt-2 border-t-2 border-orange-500">
          <Row label={hasSettled ? "Grand Total / Selling Price (Settled)" : "Grand Total / Selling Price"} value={effGrandTotal} grandTotal dataTestid="calc-grand-total" pct={effGrandTotal > 0 ? 100 : null} />
          {hasSettled && (
            <div className="text-[10px] text-emerald-700 mt-0.5 flex justify-between">
              <span>Customer settled rate · engine ₹/pc was {formatINR(grand_total)}</span>
            </div>
          )}
        </div>

        {/* Settled Development Cost REPLACES the engine Tooling / Total Development Cost in-place. */}
        {hasSettledDev ? (
          <div className="mt-3 pt-2 border-t border-dashed border-emerald-300 bg-emerald-50 -mx-2 px-2 py-2 rounded" data-testid="dev-cost-one-time-block">
            <div className="flex justify-between items-baseline">
              <span className="text-xs font-bold text-emerald-900">Development Cost (Settled)</span>
              <span className="font-mono text-sm font-bold text-emerald-900" data-testid="calc-total-development-cost">{formatINR(effDev)}</span>
            </div>
            <div className="text-[10px] text-emerald-700 mt-0.5">Customer settled · one-time, not in ₹/pc above{total_development_cost > 0 ? ` · engine tooling was ${formatINR(total_development_cost)}` : ""}</div>
          </div>
        ) : (
          <>
            {/* Tooling — separate line OR amortized note */}
            {tooling_one_time > 0 && !amortize_tooling && (
              <div className="mt-3 pt-2 border-t border-dashed border-slate-300 bg-amber-50 -mx-2 px-2 py-2 rounded" data-testid="tooling-one-time-block">
                <div className="flex justify-between items-baseline">
                  <span className="text-xs font-semibold text-amber-800">Tooling (one-time)</span>
                  <span className="font-mono text-sm font-bold text-amber-800">{formatINR(tooling_one_time)}</span>
                </div>
                <div className="text-[10px] text-amber-700 mt-0.5">Charged separately — not in ₹/pc above</div>
              </div>
            )}
            {amortize_tooling && tool_amortization > 0 && (
              <div className="mt-3 pt-2 border-t border-dashed border-slate-300 text-[10px] text-slate-500 italic" data-testid="tooling-amortized-note">
                Tooling amortized into ₹/pc · no separate one-time charge
              </div>
            )}
            {/* Other one-time development costs + Total Development Cost (one-time zone) */}
            {(other_dev_costs.length > 0 || total_development_cost > 0) && (
              <div className="mt-3 pt-2 border-t border-dashed border-slate-300 bg-amber-50 -mx-2 px-2 py-2 rounded" data-testid="dev-cost-one-time-block">
                {other_dev_costs.map((d, i) => (
                  <div key={i} className="flex justify-between items-baseline">
                    <span className="text-xs text-amber-800">{d.category || "Other"}</span>
                    <span className="font-mono text-xs text-amber-800" data-testid={`calc-dev-cost-line-${i}`}>{formatINR(d.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between items-baseline mt-1 pt-1 border-t border-amber-200">
                  <span className="text-xs font-bold text-amber-900">Total Development Cost</span>
                  <span className="font-mono text-sm font-bold text-amber-900" data-testid="calc-total-development-cost">{formatINR(total_development_cost)}</span>
                </div>
                <div className="text-[10px] text-amber-700 mt-0.5">One-time — not in ₹/pc above</div>
              </div>
            )}
          </>
        )}
      </div>

      {!hideMargin && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="space-y-1.5 text-sm">
            {targetPrice > 0 && <Row label="Target Price (cust)" value={targetPrice} dim />}
            <div className="flex justify-between">
              <span className={`text-sm ${redFlag ? "text-red-600" : "text-slate-700"}`}>Margin</span>
              <span className={`font-mono text-sm font-semibold ${redFlag ? "text-red-600" : "text-emerald-600"}`}>
                {formatINR(effMargin)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={`text-sm ${redFlag ? "text-red-600" : "text-slate-700"}`}>Margin %</span>
              <span className={`font-mono text-sm font-bold ${redFlag ? "text-red-600" : "text-emerald-600"}`} data-testid="calc-margin-pct">
                {effGrandTotal > 0 ? effMarginPct.toFixed(2) + "%" : "—"}
              </span>
            </div>
            {redFlag && (
              <div className="mt-2 flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded">
                <AlertTriangle size={13} /> Below {threshold}% margin threshold
              </div>
            )}
            {!redFlag && grand_total > 0 && (
              <div className="text-[11px] text-slate-400 mt-1">Margin is healthy — above {threshold}% threshold.</div>
            )}
          </div>
        </div>
      )}
      {hideMargin && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-50 border border-slate-200 px-2 py-2 rounded">
            <span>🔒</span> Margin details are hidden for your role.
          </div>
        </div>
      )}
      {screenshotBlock}{machineBlock}
    </div>
  );
}

function scrollToSection(id) {
  const el = typeof document !== "undefined" && document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  // Brief highlight so it's clear which section you jumped to.
  el.classList.add("ring-2", "ring-orange-400");
  setTimeout(() => el.classList.remove("ring-2", "ring-orange-400"), 1200);
}

function Row({ label, value, bold, indent, dim, highlight, section, red, grandTotal, dataTestid, target, pct }) {
  const clickable = !!target;
  const cls = [
    "flex justify-between items-baseline py-0.5",
    indent ? "pl-2" : "",
    highlight ? "bg-slate-50 -mx-2 px-2 py-1 rounded" : "",
    section ? "border-t border-slate-100 pt-1 mt-0.5" : "",
    clickable ? "cursor-pointer hover:bg-orange-50 -mx-2 px-2 rounded transition-colors" : "",
  ].join(" ");
  const labelCls = [
    "text-sm",
    dim ? "text-slate-400" : "text-slate-700",
    bold ? "font-medium" : "",
    red ? "text-red-600" : "",
    grandTotal ? "font-bold text-slate-900" : "",
    clickable ? "hover:text-orange-700" : "",
  ].join(" ");
  const valCls = [
    "font-mono text-sm",
    dim ? "text-slate-400" : "text-slate-900",
    bold ? "font-semibold" : "",
    red ? "text-red-600" : "",
    grandTotal ? "text-base font-bold text-orange-600" : "",
  ].join(" ");
  return (
    <div
      className={cls}
      onClick={clickable ? () => scrollToSection(target) : undefined}
      title={clickable ? "Go to this section" : undefined}
      data-testid={target ? `calc-jump-${target}` : undefined}
    >
      <span className={labelCls}>{label}</span>
      <span className="flex items-baseline gap-2">
        {pct != null && (
          <span
            className={`font-mono text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded ${grandTotal ? "bg-orange-100 text-orange-700" : "bg-indigo-50 text-indigo-600"}`}
            title="Share of Grand Total"
          >
            {pct.toFixed(1)}%
          </span>
        )}
        <span className={valCls} data-testid={dataTestid}>{formatINR(value)}</span>
      </span>
    </div>
  );
}
